import { useNavigate, useParams } from "react-router-dom";
import { InquiryEditForm } from "@/components/inquiry/inquiry-edit-form";
import { InquiryFormData, TourPackageItem, InquiryDayItem } from "@/types/database";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

export function InquiryEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Fetch tour packages for name lookup
  const { data: tourPackages = [] } = useQuery<TourPackageItem[]>({
    queryKey: ["tourPackages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tour_package")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  // Fetch existing inquiry data
  const { data: inquiry, isLoading } = useQuery({
    queryKey: ["inquiry", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inquiry")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      
      // Add debug log to see what's coming from the database
      console.log("Raw inquiry data from DB:", JSON.stringify(data, null, 2));
      
      return data;
    },
  });

  // Debug component to display raw data
  const DebugPanel = ({ data }: { data: any }) => (
    <div className="mt-8 p-4 border border-gray-300 rounded bg-gray-50 print:hidden">
      <h3 className="text-lg font-semibold mb-2">Debug Information</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-medium mb-1">Raw Inquiry Data:</h4>
          <pre className="text-xs bg-white p-2 rounded overflow-auto max-h-96">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
        <div>
          <h4 className="text-sm font-medium mb-1">Parsed Days:</h4>
          <pre className="text-xs bg-white p-2 rounded overflow-auto max-h-96">
            {JSON.stringify(data?.tour_package_json, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );

  const handleSubmit = async (formData: any) => {
    try {
      // Calculate all the totals first
      const totalTransportCost = formData.days.reduce((sum: number, day: any) => {
        return sum + (typeof day.transport_cost === 'number' ? day.transport_cost : Number(day.transport_cost) || 0);
      }, 0);
      
      const totalParkingCost = formData.days.reduce((sum: number, day: any) => {
        return sum + (typeof day.parking_cost === 'number' ? day.parking_cost : Number(day.parking_cost) || 0);
      }, 0);
      
      const totalAdultEntranceFee = formData.days.reduce((sum: number, day: any) => {
        if (!day.captured_tours) return sum;
        
        const dayKey = `day_${day.day_number}`;
        if (!day.captured_tours[dayKey] || !day.captured_tours[dayKey].entrance_adult) return sum;
        
        // Get all tour keys from entrance_adult object
        const tourKeys = Object.keys(day.captured_tours[dayKey].entrance_adult);
        
        return sum + tourKeys.reduce((daySum: number, tour: string) => {
          const entranceFee = day.captured_tours[dayKey].entrance_adult[tour];
          const numericFee = typeof entranceFee === 'number' ? entranceFee : Number(entranceFee) || 0;
          return daySum + (numericFee * formData.adult_count);
        }, 0);
      }, 0);
      
      const totalChildEntranceFee = formData.days.reduce((sum: number, day: any) => {
        if (!day.captured_tours) return sum;
        
        const dayKey = `day_${day.day_number}`;
        if (!day.captured_tours[dayKey] || !day.captured_tours[dayKey].entrance_child) return sum;
        
        // Get all tour keys from entrance_child object
        const tourKeys = Object.keys(day.captured_tours[dayKey].entrance_child);
        
        return sum + tourKeys.reduce((daySum: number, tour: string) => {
          const entranceFee = day.captured_tours[dayKey].entrance_child[tour];
          const numericFee = typeof entranceFee === 'number' ? entranceFee : Number(entranceFee) || 0;
          return daySum + (numericFee * formData.child_count);
        }, 0);
      }, 0);
      
      const totalMealCost = formData.days.reduce((sum: number, day: any) => {
        return sum + (typeof day.meal_cost === 'number' ? day.meal_cost : Number(day.meal_cost) || 0);
      }, 0);
      
      const totalOtherCost = formData.days.reduce((sum: number, day: any) => {
        return sum + (typeof day.other_cost === 'number' ? day.other_cost : Number(day.other_cost) || 0);
      }, 0);
      
      const totalHotelPrice = formData.hotels.reduce((sum: number, hotel: any) => {
        return sum + (typeof hotel.total_price === 'number' ? hotel.total_price : Number(hotel.total_price) || 0);
      }, 0);
      
      // Calculate total entrance fee and grand total
      const totalEntranceFee = totalAdultEntranceFee + totalChildEntranceFee;
      const grandTotal = totalTransportCost + totalParkingCost + totalEntranceFee + totalMealCost + totalOtherCost + totalHotelPrice;
      
      // Calculate totals with proper type assertions and null checks
      const totalTransport = totalTransportCost || 0;
      const totalParking = totalParkingCost || 0;
      const totalAdultEntrance = totalAdultEntranceFee || 0;
      const totalChildEntrance = totalChildEntranceFee || 0;
      const totalEntrance = totalAdultEntrance + totalChildEntrance;
      const totalMeal = totalMealCost || 0;
      const totalOther = totalOtherCost || 0;
      const totalHotel = totalHotelPrice || 0;
      const grandTotalCalculated = totalTransport + totalParking + totalEntrance + totalMeal + totalOther + totalHotel;
      
      // Prepare the data for submission
      const submissionData: any = {
        name: formData.guest_name,
        date: formData.start_date,
        start_date: formData.start_date,
        adult_count: formData.adult_count,
        child_count: formData.child_count,
        // Remove the 'days' field as it doesn't exist in the database schema
        nights: formData.nights,
        status: formData.status,
        hotel_type: formData.hotel_type,
        car_type: formData.car_type,
        remarks: formData.remarks,
        flight_information: formData.flight_information,
        // No automatic USD calculation - just pass through the value from the form
        total_usd: formData.total_usd,
        
        // Convert days array to tour_package_json format
        tour_package_json: formData.days.reduce((acc: any, day: any) => {
          const dayKey = `day_${day.day_number}`;
          
          // Extract entrance fees and parking from captured_tours
          let entranceAdult = {};
          let entranceChild = {};
          let parking = {};
          let tours = [];
          
          if (day.captured_tours && day.captured_tours[dayKey]) {
            entranceAdult = day.captured_tours[dayKey].entrance_adult || {};
            entranceChild = day.captured_tours[dayKey].entrance_child || {};
            parking = day.captured_tours[dayKey].parking || {};
            tours = day.captured_tours[dayKey].tours || [];
          }
          
          // Ensure we're preserving all entrance fee data
          acc[dayKey] = {
            tours: tours,
            entrance_adult: entranceAdult,
            entrance_child: entranceChild,
            parking: parking,
            transport_area: day.transport_area,
            transport_cost: day.transport_cost,
            meal_cost: day.meal_cost,
            other_cost: day.other_cost,
            tour_package_name: day.tour_package_name,
            tour_package_id: day.tour_package_id,
            captured_tours: day.captured_tours, // Include the full captured_tours object
            itinerary: day.captured_tours?.[dayKey]?.itinerary || '',
            day_date: day.day_date || '',
            note: day.note || ''
          };
          return acc;
        }, {}),
        
        // Convert hotels array to hotel_json format
        hotel_json: {
          hotels: formData.hotels.reduce((acc: any, hotel: any, index: number) => {
            acc[`hotel_${index + 1}`] = {
              hotel_name: hotel.hotel_name,
              checkin_date: hotel.checkin_date,
              checkout_date: hotel.checkout_date,
              nights: hotel.nights,
              room_rate: hotel.room_rate,
              room_count: hotel.room_count,
              total_price: hotel.total_price,
            };
            return acc;
          }, {})
        },
        
        // Use the pre-calculated totals
        total_transport_cost: totalTransport,
        total_parking_cost: totalParking,
        total_adult_entrance_fee: totalAdultEntrance,
        total_child_entrance_fee: totalChildEntrance,
        total_entrance_fee: totalEntrance,
        total_meal_cost: totalMeal,
        total_other_cost: totalOther,
        total_hotel_price: totalHotel,
        grand_total: grandTotalCalculated
      };
      
      console.log("Submitting data:", submissionData);
      
      // Update the inquiry
      const { error } = await supabase
        .from("inquiry")
        .update(submissionData)
        .eq("id", id);
      
      if (error) throw error;
      
      toast.success("Inquiry updated successfully");
      navigate(`/inquiry/${id}/agent`);
    } catch (error: any) {
      console.error("Error updating inquiry:", error);
      toast.error(`Error updating inquiry: ${error.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (!inquiry) {
    return <div>Inquiry not found</div>;
  }

  // Transform inquiry data to form data format
  const initialData: InquiryFormData = {
    guest_name: inquiry.name || "",
    start_date: inquiry.start_date,
    adult_count: inquiry.adult_count,
    child_count: inquiry.child_count,
    nights: inquiry.nights,
    status: inquiry.status,
    hotel_type: inquiry.hotel_type,
    car_type: inquiry.car_type || "MPV",
    remarks: inquiry.remarks || "",
    flight_information: inquiry.flight_information || "",
    tour_package_json: inquiry.tour_package_json || {},
    hotel_json: inquiry.hotel_json || { hotels: {} },
    total_usd: inquiry.total_usd || 0,
    days: [],
    hotels: [],
    total_transport_cost: inquiry.total_transport_cost || 0,
    total_parking_cost: inquiry.total_parking_cost || 0,
    total_adult_entrance_fee: inquiry.total_adult_entrance_fee || 0,
    total_child_entrance_fee: inquiry.total_child_entrance_fee || 0,
    total_entrance_fee: inquiry.total_entrance_fee || 0,
    total_meal_cost: inquiry.total_meal_cost || 0,
    total_other_cost: inquiry.total_other_cost || 0,
    total_hotel_price: inquiry.total_hotel_price || 0,
    grand_total: inquiry.grand_total || 0
  };

  // Convert tour_package_json to days array
  if (inquiry.tour_package_json) {
    // Create a properly typed days array
    const daysArray: InquiryDayItem[] = Object.entries(inquiry.tour_package_json).map(([dayKey, dayData]: [string, any]) => {
      const dayNumber = parseInt(dayKey.replace('day_', ''));
      
      // Create a proper captured_tours structure if it's missing
      let captured_tours = {};
      
      // Check if we have tours data to create captured_tours
      if (dayData.tours && Array.isArray(dayData.tours)) {
        captured_tours = {
          [`day_${dayNumber}`]: {
            title: `Day ${dayNumber} (${dayData.day_date || ''}) - ${dayData.tour_package_name || ''}`,
            tours: dayData.tours || [],
            entrance_adult: dayData.entrance_adult || {},
            entrance_child: dayData.entrance_child || {},
            parking: dayData.parking || {},
            tour_package_name: dayData.tour_package_name || '',
            itinerary: dayData.itinerary || '',
            note: dayData.note || ''
          }
        };
        
        console.log(`Created captured_tours for day ${dayNumber} with entrance fees and parking data`);
      } else if (dayData.captured_tours) {
        // If captured_tours already exists, use it
        captured_tours = dayData.captured_tours;
        
        // Make sure entrance fees and parking data are properly initialized
        if (!captured_tours[`day_${dayNumber}`].entrance_adult) {
          captured_tours[`day_${dayNumber}`].entrance_adult = dayData.entrance_adult || {};
        }
        if (!captured_tours[`day_${dayNumber}`].entrance_child) {
          captured_tours[`day_${dayNumber}`].entrance_child = dayData.entrance_child || {};
        }
        if (!captured_tours[`day_${dayNumber}`].parking) {
          captured_tours[`day_${dayNumber}`].parking = dayData.parking || {};
        }
        
        console.log(`Using existing captured_tours for day ${dayNumber}`);
      }
      
      // Create a complete InquiryDayItem with all required fields
      return {
        id: `temp_${dayNumber}`, // Add a temporary ID to satisfy the type requirement
        inquiry_id: inquiry.id,
        day_number: dayNumber,
        tour_package_id: dayData.tour_package_id || '',
        tour_package_name: dayData.tour_package_name || '',
        transport_area: dayData.transport_area || 'Kuta',
        transport_cost: dayData.transport_cost || 0,
        meal_cost: dayData.meal_cost || 0,
        other_cost: dayData.other_cost || 0,
        day_date: dayData.day_date || '',
        captured_tours: captured_tours,
        is_edited: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        car_type: inquiry.car_type || 'MPV',
        title: `Day ${dayNumber} (${dayData.day_date || ''}) - ${dayData.tour_package_name || ''}`,
        parking_cost: Object.values(dayData.parking || {}).reduce((sum: number, cost: any) => {
          return sum + (typeof cost === 'number' ? cost : Number(cost) || 0);
        }, 0) as number, // Calculate parking cost from the parking data and explicitly cast to number
        note: dayData.note || ""
      };
    });
    
    initialData.days = daysArray;
  } else if (inquiry.inquiry_day) {
    // For backward compatibility, convert old inquiry_day structure to new format
    const daysArray: InquiryDayItem[] = inquiry.inquiry_day.map(day => {
      // Create proper captured_tours structure if missing
      let captured_tours = day.captured_tours || {};
      
      if (!captured_tours[`day_${day.day_number}`]) {
        const tourPackage = tourPackages.find(p => p.id === day.tour_package_id);
        if (tourPackage) {
          // Initialize captured_tours with basic structure
          captured_tours = {
            [`day_${day.day_number}`]: {
              title: `Day ${day.day_number} (${day.day_date || ''}) - ${tourPackage.tour_package_name}`,
              tours: [],
              entrance_adult: {},
              entrance_child: {},
              parking: {},
              tour_package_name: tourPackage.tour_package_name,
              itinerary: tourPackage.itinerary || ''
            }
          };
          console.log(`Fixed missing captured_tours for day ${day.day_number}`);
        }
      }
      
      // Ensure all required fields are present
      return {
        ...day,
        captured_tours,
        // Add any missing required fields with default values
        id: day.id || `temp_${day.day_number}`,
        inquiry_id: day.inquiry_id || inquiry.id,
        created_at: day.created_at || new Date().toISOString(),
        updated_at: day.updated_at || new Date().toISOString(),
        car_type: day.car_type || inquiry.car_type || 'MPV',
        parking_cost: day.parking_cost || 0
      };
    });
    
    initialData.days = daysArray;
  }

  // Convert hotel_json to hotels array
  if (inquiry.hotel_json && inquiry.hotel_json.hotels) {
    initialData.hotels = Object.values(inquiry.hotel_json.hotels).map((hotel: any) => ({
      hotel_name: hotel.hotel_name,
      checkin_date: hotel.checkin_date,
      checkout_date: hotel.checkout_date,
      nights: hotel.nights,
      room_rate: hotel.room_rate,
      room_count: hotel.room_count,
      total_price: hotel.total_price
    }));
  } else if (inquiry.inquiry_hotel) {
    // For backward compatibility
    initialData.hotels = inquiry.inquiry_hotel;
  }

  console.log("Transformed initial data:", initialData);

  return (
    <div className="container mx-auto py-10">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/inquiry")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-3xl font-bold">Edit Inquiry - {inquiry.voucher_code}</h1>
      </div>

      <div className="">
        <InquiryEditForm initialData={initialData} onSubmit={handleSubmit} />
      </div>
      
      {/* Add debug panel */}
      <DebugPanel data={inquiry} />
    </div>
  );
}
